import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { Observation } from "@medplum/fhirtypes";

import { Flex, Paper } from "@mantine/core";

import {
  CLIENT_POSTPARTUM_FLOWSHEET_MENU_BUTTONS,
  CLIENT_POSTPARTUM_FLOWSHEET_QUICK_BUTTONS,
} from "@/encounters/components/EncounterLabor/constants/postpartum-flowsheets";
import ClientPostpartumFlowsheetTable from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/ClientPostpartum/ClientTable";
import PostpartumFlowsheetHeader from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/PostpartumHeader/PostpartumFlowsheetHeader";
import { usePostpartumObservations } from "@/encounters/components/EncounterLabor/hooks/usePostpartumObservations";
import { LOINCCodes } from "@/fhir/enums/loinc";
import { selectPostpartumObservations } from "@/store/slices/postpartum-observations";

interface ClientPostpartumProps {
  encounterId: string;
  clientId: string;
  onBirthRefresh?: (isRetry?: boolean) => Promise<void>;
  onBirthCreate: (birthDateTime: string) => void;
  isViewOnly: boolean;
}

export default function ClientPostpartum({
  encounterId,
  clientId,
  onBirthRefresh,
  onBirthCreate,
  isViewOnly,
}: ClientPostpartumProps) {
  const [showNumbersOnly, setShowNumbersOnly] = useState(false);
  const { refreshObservations } = usePostpartumObservations(encounterId, clientId);

  // Get postpartum observations from Redux
  const postpartumObservations = useSelector(selectPostpartumObservations);

  // Listen for CBL observations in Redux state
  useEffect(() => {
    // Safety check: ensure Redux state is properly initialized
    if (!postpartumObservations) {
      return;
    }

    // Get observations array from Redux state (same pattern as usePostpartumRows)
    const observationsArray = Object.values(postpartumObservations) as Observation[];

    // Check if we have CBL observations in Redux state
    const hasCblObservations = observationsArray.some((obs) =>
      obs?.code?.coding?.some((coding) => coding.code === LOINCCodes.CumulativeBloodLoss),
    );

    // If we have CBL observations, the UI will automatically update
    // No need to manually refresh since Redux state change triggers re-render
    if (hasCblObservations) {
      // CBL observations are now available in Redux, UI will re-render automatically
    }
  }, [postpartumObservations, encounterId, clientId]);

  return (
    <Flex bg="white.0" align="center" justify="center" direction="column">
      <Paper py="lg" w="97%" radius="xl" bg="white.0">
        <PostpartumFlowsheetHeader
          clientId={clientId}
          isViewOnly={isViewOnly}
          encounterId={encounterId}
          onRefresh={refreshObservations}
          showNumbersOnly={showNumbersOnly}
          title="Client Postpartum Flow Sheet"
          setShowNumbersOnly={setShowNumbersOnly}
          menuButtons={CLIENT_POSTPARTUM_FLOWSHEET_MENU_BUTTONS}
          quickButtons={CLIENT_POSTPARTUM_FLOWSHEET_QUICK_BUTTONS}
        />
        <ClientPostpartumFlowsheetTable
          clientId={clientId}
          isViewOnly={isViewOnly}
          encounterId={encounterId}
          showNumbersOnly={showNumbersOnly}
        />
      </Paper>
    </Flex>
  );
}
